import Button from '@/src/components/atom/Button';
import LucideIcon from '@/src/components/atom/Icons/LucideIcon';
import BottomFixedContainer from '@/src/components/molecule/BottomFixedContainer';
import MainContentTitle from '@/src/components/molecule/MainContentTitle';
import { BUTTON_CONST } from '@/src/constants/buttons';
import PATH from '@/src/routes/path';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { DetailSctionRenderer } from '@/src/components/organism/DetailSection/renderer';
import DetailSection from '@/src/components/organism/DetailSection';
import { NAV_CONST } from '@/src/constants/nav';
import dayjs from 'dayjs';
import { delteBannerByIdAPI, getBannerByIdAPI } from '@/src/apis/rest/banner';
import {
  getConnectedContent,
  getConnectedContentDetail,
} from '@/src/models/BannerDto';
import { OPTIONS_CONST } from '@/src/constants/options';
import AlertDialog from '@/src/components/atom/AlertDialog';
import useModalState from '@/src/hooks/useModalState';
import { toast } from '@/src/hooks/useToast';
import { TOAST_CONST } from '@/src/constants/toast';

type ModalState = 'NONE' | 'DELETE';

const LIST_PATH = PATH.MAIN_BANNER_LIST;
const BACK_PATH = PATH.MAIN_BANNER_LIST;
const EDIT_PATH = PATH.MAIN_BANNER_EDIT;

export default function MainBannerDetailPage() {
  // util
  const navigate = useNavigate();
  const { id } = useParams();

  // state
  const { modalState, handleOpenModal, handleToggleModal } =
    useModalState<ModalState>();

  // query
  const { data: bannerData } = useQuery({
    queryKey: ['banner', id],
    queryFn: () => getBannerByIdAPI({ id: Number(id) }),
    meta: {
      backUrl: BACK_PATH,
      callback: () => {
        navigate(BACK_PATH, { replace: true });
      },
    },
  });

  // mutation
  const deleteBannerMutation = useMutation({ mutationFn: delteBannerByIdAPI });

  // event
  const handleGoBack = () => {
    navigate(-1);
  };
  const handleDeleteBanner = () => {
    deleteBannerMutation.mutate(
      { id: Number(id) },
      {
        onSuccess: () => {
          toast({
            variant: 'success',
            title: TOAST_CONST.deleteSuccess.title,
            description: TOAST_CONST.deleteSuccess.description,
          });
          setTimeout(() => {
            navigate(LIST_PATH, { replace: true });
          }, 0);
        },
        onError: e => {
          toast({
            variant: 'error',
            title: TOAST_CONST.error.title,
            description: e.message,
          });
        },
      },
    );
  };

  // render
  const renderBottomFixedContent = () => {
    return (
      <BottomFixedContainer>
        <div className="flex justify-between">
          <Button
            className="w-max"
            buttonSize="medium"
            buttonColor="outline"
            onClick={handleGoBack}
          >
            <div className="flex items-center gap-x-2">
              <LucideIcon name="ChevronLeft" size={16} />
              {BUTTON_CONST.backToScreen}
            </div>
          </Button>
          <div className="flex items-center gap-2">
            <Button
              className="w-40"
              buttonSize="medium"
              buttonColor="red"
              onClick={() => handleOpenModal('DELETE')}
              isLoading={deleteBannerMutation.isPending}
            >
              {BUTTON_CONST.delete}
            </Button>
            <Link to={EDIT_PATH.replace(':id', id || '')}>
              <Button className="w-40" buttonSize="medium">
                {BUTTON_CONST.editShort}
              </Button>
            </Link>
          </div>
        </div>
      </BottomFixedContainer>
    );
  };

  return (
    <>
      <MainContentTitle
        breadcrumbList={[NAV_CONST.mainBanner, NAV_CONST.mainBannerDetail]}
        pageTitle={NAV_CONST.mainBannerDetail}
      />
      <div className="mb-[100px] flex flex-col gap-4">
        <DetailSection
          sectionTitle={
            <div className="flex w-full items-center justify-between">
              <span className="font-pretendardSemiBold text-b18sb">
                메인 배너 정보
              </span>
            </div>
          }
          data={bannerData}
          contents={[
            [
              {
                key: 'postedAt',
                lable: '게시 기간',
                valueSetter: data =>
                  `${dayjs(data?.bannerPostStartedAt).format(
                    'YYYY-MM-DD',
                  )} ~ ${dayjs(data?.bannerPostEndedAt).format('YYYY-MM-DD')}`,
              },
              {
                key: 'bannerShowedYn',
                lable: '노출 여부',
                valueSetter: data => (data?.bannerShowedYn ? 'true' : 'false'),
                render: DetailSctionRenderer.BADGE({
                  options: [
                    {
                      label: OPTIONS_CONST.showed,
                      key: 'true',
                      badgeColor: 'lightGreen',
                    },
                    {
                      label: OPTIONS_CONST.notShowed,
                      key: 'false',
                      badgeColor: 'lightGray',
                    },
                  ],
                }),
              },
              {
                key: 'bannerTitle',
                lable: '제목',
              },
              {
                key: 'bannerImage',
                lable: '배너 이미지',
                valueSetter: data => data?.bannerImage?.bufferFullUrl || '',
                render: DetailSctionRenderer.IMAGE({ width: 335, height: 188 }),
              },
            ],
            [
              {
                key: 'bannerContent',
                lable: '컨텐츠 유형',
                valueSetter: data =>
                  data ? getConnectedContent(data, false) : '-',
              },
              {
                key: 'bannerConnectedContent',
                lable: '연결 컨텐츠',
                valueSetter: data =>
                  data ? getConnectedContentDetail(data) : '-',
                render: (value: string) =>
                  bannerData?.bannerContentType ===
                  'bannerExternalUrlConnection' ? (
                    <a
                      className="text-b16r text-gray6 underline"
                      href={value}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {value}
                    </a>
                  ) : (
                    value
                  ),
              },
              {
                key: 'bannerMarketingYn',
                lable: '광고 여부',
                valueSetter: data => {
                  return data?.bannerMarketingYn ? 'true' : 'false';
                },
                render: DetailSctionRenderer.BADGE({
                  options: [
                    {
                      label: OPTIONS_CONST.advertisement,
                      key: 'true',
                      badgeColor: 'lightGreen',
                    },
                    {
                      label: OPTIONS_CONST.notAdvertisement,
                      key: 'false',
                      badgeColor: 'lightGray',
                    },
                  ],
                }),
              },
              {
                key: 'bannerViewHistoryCount',
                lable: '클릭 수',
                valueSetter: data => {
                  return (data?.bannerViewHistoryCount || 0).toLocaleString();
                },
                render: DetailSctionRenderer.PREFIX_SUFFIX({ suffix: '회' }),
              },
              {
                key: 'createdAt',
                lable: '등록 일시',
                render: DetailSctionRenderer.DATE({
                  format: 'YYYY-MM-DD HH:mm:ss',
                }),
              },
              {
                key: ['administrator', 'name'],
                lable: '등록 관리자',
              },
            ],
          ]}
        />
      </div>
      {renderBottomFixedContent()}

      {/* 모달 */}
      {modalState === 'DELETE' && (
        <AlertDialog
          open={modalState === 'DELETE'}
          onOpenChange={() => handleToggleModal('DELETE')}
          okText={BUTTON_CONST.ok}
          onOk={handleDeleteBanner}
          title="배너 삭제"
          okButtonProps={{
            isLoading: deleteBannerMutation.isPending,
          }}
        >
          <p className="whitespace-pre-wrap">
            배너를 삭제하시겠습니까?\n삭제된 배너는 사용자에게 노출되지
            않습니다.
          </p>
        </AlertDialog>
      )}
    </>
  );
}
